/**
 * Authorization code with PKCE, the parts worth testing on their own.
 *
 * A public client, because this runs in a browser and a browser cannot keep a secret. PKCE is what replaces
 * one: the verifier never leaves this origin, the challenge does, and a stolen authorization code is useless
 * without the verifier that produced its challenge.
 *
 * ## What is persisted, and why exactly this much
 *
 * The verifier and the state have to survive a full page unload - the browser leaves for the identity
 * provider and comes back - so they cannot live in memory. They go in `sessionStorage`, and that is the only
 * thing this application persists anywhere:
 *
 *   - it is scoped to one tab and is gone when the tab closes;
 *   - the verifier is single-use, bound to one authorization request, and worthless without the code;
 *   - neither value grants any access to `/api/v1`.
 *
 * The access token is deliberately *not* here. It stays in memory, so a reload signs the representative in
 * again through the identity provider rather than reading a credential back off the disk. The rejected
 * alternative was persisting a refresh token, which is a durable credential sitting in storage that any
 * script on this origin can read, in exchange for saving one click.
 */

const STORE_KEY = 'ddds.auth.pending';

export interface PendingAuthorization {
  /** Proves this callback belongs to the request we started. The CSRF control for the flow. */
  readonly state: string;
  /** Never sent to the authorization endpoint; only to the token endpoint. */
  readonly verifier: string;
  /** Where to go once signed in, so a deep link survives the round trip. */
  readonly returnTo: string;
}

function base64url(bytes: Uint8Array): string {
  let text = '';
  // `fromCodePoint` rather than `fromCharCode`. Identical for the 0-255 these are, and correct for the
  // reason `fromCharCode` is not: it takes a code point rather than a UTF-16 code unit, so it cannot
  // silently truncate a value above 0xFFFF if this is ever handed something wider than a byte.
  for (const byte of bytes) text += String.fromCodePoint(byte);
  // The trailing `=` uses `replace` deliberately: `/=+$/` is anchored, so there is exactly one match and
  // `replaceAll` would need a global flag that says the opposite of what is meant.
  return btoa(text).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/, '');
}

/** 32 bytes of CSPRNG output. RFC 7636 allows 43 to 128 characters; this is 43. */
export function randomVerifier(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64url(bytes);
}

export function randomState(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return base64url(bytes);
}

/**
 * S256, never `plain`.
 *
 * `plain` sends the verifier itself as the challenge, which defeats the entire mechanism: anyone who
 * intercepted the authorization request would hold what the token request needs. The realm sets
 * `pkce.code.challenge.method: S256` so the provider refuses `plain` as well, and both ends refusing is the
 * right shape for something this cheap.
 */
export async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64url(new Uint8Array(digest));
}

export function rememberPending(pending: PendingAuthorization): void {
  sessionStorage.setItem(STORE_KEY, JSON.stringify(pending));
}

/**
 * Read the pending authorization and remove it in the same breath.
 *
 * Removed unconditionally, including when it fails to parse. A verifier left behind is a verifier a second
 * callback could try to use, and this flow has exactly one legitimate callback.
 */
export function takePending(): PendingAuthorization | undefined {
  const raw = sessionStorage.getItem(STORE_KEY);
  sessionStorage.removeItem(STORE_KEY);
  if (raw === null) return undefined;

  try {
    const parsed = JSON.parse(raw) as Partial<PendingAuthorization>;
    if (typeof parsed.state !== 'string' || typeof parsed.verifier !== 'string') return undefined;
    return {
      state: parsed.state,
      verifier: parsed.verifier,
      returnTo: typeof parsed.returnTo === 'string' ? parsed.returnTo : '/',
    };
  } catch {
    return undefined;
  }
}

export interface AuthorizeRequest {
  readonly authorizationEndpoint: string;
  readonly clientId: string;
  readonly redirectUri: string;
  readonly state: string;
  readonly challenge: string;
  /**
   * Space-separated, from runtime configuration rather than compiled in.
   *
   * A parameter because the right value is a property of the tenant, not of this code: Keycloak's realm
   * carries an audience mapper so `openid profile email` suffices, while Entra and Okta need the API's own
   * scope named or they mint a token for something else entirely. Hardcoding one would make this a
   * customer-specific bundle, which is the same objection D-01 raises to baking in an origin.
   */
  readonly scope: string;
}

/**
 * The URL to send the browser to.
 *
 * A pure function returning a string rather than something that navigates, so the parameters can be
 * asserted without a browser. Getting `code_challenge_method` or `response_type` wrong produces a flow that
 * appears to work right up until the token exchange.
 */
export function authorizationUrl(request: AuthorizeRequest): string {
  const url = new URL(request.authorizationEndpoint);
  url.searchParams.set('client_id', request.clientId);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', request.scope);
  url.searchParams.set('redirect_uri', request.redirectUri);
  url.searchParams.set('state', request.state);
  url.searchParams.set('code_challenge', request.challenge);
  url.searchParams.set('code_challenge_method', 'S256');
  return url.toString();
}

/**
 * The code and state from a callback URL, or undefined when this is not one.
 *
 * Takes a string rather than reading `location`, so the failure cases - an error from the provider, a state
 * that does not match, a callback with no code - are testable without navigation.
 */
export function callbackParams(href: string): { code?: string; state?: string; error?: string } | undefined {
  const url = new URL(href);
  const code = url.searchParams.get('code');
  const state = url.searchParams.get('state');
  const error = url.searchParams.get('error');

  if (code === null && error === null) return undefined;
  return {
    ...(code === null ? {} : { code }),
    ...(state === null ? {} : { state }),
    ...(error === null ? {} : { error }),
  };
}
